import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';

import exceedsIntegerDigits from 'services/numbers/exceedsIntegerDigits';
import isNumber from 'services/numbers/isNumber';

import 'style/layout/form/NumberWithUnitInputField.css';

const isValid = (value: string, required?: boolean) =>
  (!required && value === '') || isNumber(value);

// An estimate of the width of the text in the font of the element (about 0.6 em per
// character, a little more than the units need), used while the element is hidden
const estimateTextWidth = (element: Element, text: string): number =>
  Math.ceil(
    text.length *
      0.6 *
      (parseFloat(window.getComputedStyle(element).fontSize) || 16)
  );

export default ({
  bsSize,
  id,
  label,
  maxIntegerDigits,
  name,
  onBlur,
  onChange,
  disabled,
  required,
  unit,
  value,
}: {
  bsSize?: 'lg' | 'sm';
  id?: string;
  label?: string;
  // Typing more digits before the decimals than this is ignored
  maxIntegerDigits?: number;
  onBlur?: () => void;
  name?: string;
  onChange?: any;
  disabled?: boolean;
  required?: boolean;
  unit: string;
  value: string;
}): JSX.Element => {
  const baseInputUnitPadding = 15;

  const [inputUnitPadding, setInputUnitPadding] = useState<string>(
    `${baseInputUnitPadding}px`
  );

  const [valid, setValid] = useState<boolean>(false);

  const unitElementRef = useRef<HTMLParagraphElement>(null);

  const updateInputUnitPadding = () => {
    if (!valid) {
      return;
    }

    const unitElement = unitElementRef.current;

    if (unitElement === null) {
      setInputUnitPadding(`${baseInputUnitPadding}px`);
      return;
    }

    // A hidden field (e.g. rendered before it is shown) has no width yet, so the unit
    // is then estimated from its text and font; it is measured again when shown
    const width =
      unitElement.clientWidth || estimateTextWidth(unitElement, unit ?? '');

    setInputUnitPadding(`${baseInputUnitPadding + width}px`);
  };

  useEffect(() => {
    const validStatus = isValid(value, required);
    setValid(validStatus);
    if (!validStatus) {
      setInputUnitPadding(`${baseInputUnitPadding + 20}px`);
    }
  }, [required, value]);

  useLayoutEffect(() => {
    updateInputUnitPadding();
  }, [unit, valid, value]);

  // The unit is measured again when its size changes, e.g. when the field was hidden
  // while first rendered (width 0) or the web font loads later; otherwise the number
  // would run under the unit
  useEffect(() => {
    const unitElement = unitElementRef.current;
    if (unitElement === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => updateInputUnitPadding());
    observer.observe(unitElement);

    return () => observer.disconnect();
  }, [valid]);

  // The web font may change the width of the unit after it has been measured
  useEffect(() => {
    let active = true;
    document.fonts?.ready.then(() => {
      if (active) {
        updateInputUnitPadding();
      }
    });

    return () => {
      active = false;
    };
  }, [valid]);

  return (
    <FormGroup className="number-with-unit-input-field">
      {label === undefined ? null : (
        <Label className="label" for={id}>
          {label}
        </Label>
      )}
      <Input
        bsSize={bsSize}
        className="text-field align-right"
        id={id}
        invalid={!valid}
        name={name}
        onBlur={onBlur}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (!exceedsIntegerDigits(event.target.value, maxIntegerDigits)) {
            onChange?.(event);
          }
        }}
        disabled={disabled}
        required={required}
        style={{ paddingRight: inputUnitPadding }}
        type="text"
        value={value}
      />
      <p className="text-field" ref={unitElementRef}>
        {valid ? unit : null}
      </p>
    </FormGroup>
  );
};
